import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type {
  ExecuteScenarioRequest,
  ListOrgMachinesResponse,
  ScenarioStructure,
  SimulationResult,
  UpdateScenarioStructureResponse,
} from "@opus-dt/shared-types";
import type { ScenarioDetail } from "@/types/sim-sandbox.types";

// Hooks de API do sandbox (contract-first: z.infer de @opus-dt/shared-types).
// Tipos de domínio canônicos vivem em types/sim-sandbox.types.ts (re-export para consumidores).
export type { ScenarioDetail };

export function useScenarioStructure(scenarioId: string | undefined) {
  return useQuery<ScenarioDetail>({
    queryKey: ["scenarios", scenarioId],
    queryFn: () => api.get<ScenarioDetail>(`/api/v1/scenarios/${scenarioId}`),
    enabled: !!scenarioId,
  });
}

export function useSaveScenarioStructure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      scenarioId,
      structure,
      baseUpdatedAt,
    }: {
      scenarioId: string;
      structure: ScenarioStructure;
      baseUpdatedAt?: string | null;
    }) =>
      api.patch<UpdateScenarioStructureResponse>(`/api/v1/scenarios/${scenarioId}/structure`, {
        structure,
        ...(baseUpdatedAt ? { baseUpdatedAt } : {}),
      }),
    onSuccess: (_data, { scenarioId }) => {
      void qc.invalidateQueries({ queryKey: ["scenarios", scenarioId] });
      void qc.invalidateQueries({ queryKey: ["simulations"] });
    },
    // Consumidor `sandbox/hooks/useSandboxController.ts` (saveStructure) já trata.
    meta: { skipErrorToast: true },
  });
}

export function useExecuteScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      scenarioId,
      period,
      isBaseline,
    }: { scenarioId: string } & ExecuteScenarioRequest) =>
      api.post<SimulationResult>(`/api/v1/scenarios/${scenarioId}/executions`, {
        period,
        isBaseline,
      }),
    onSuccess: (_data, { scenarioId }) => {
      void qc.invalidateQueries({ queryKey: ["simulations"] });
      void qc.invalidateQueries({ queryKey: ["scenarios", scenarioId] });
    },
    // Consumidor `sandbox/hooks/useScenarioRun.ts` já trata localmente (inclusive 409
    // SCENARIO_PRODUCT_INCOMPATIBLE).
    meta: { skipErrorToast: true },
  });
}

export interface OrgMachinesParams {
  search?: string;
  compatibleWithProductId?: string;
  page?: number;
  pageSize?: number;
}

export function useOrgMachines(params: OrgMachinesParams, enabled = true) {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.compatibleWithProductId)
    qs.set("compatibleWithProductId", params.compatibleWithProductId);
  qs.set("page", String(params.page ?? 1));
  qs.set("pageSize", String(params.pageSize ?? 50));
  return useQuery<ListOrgMachinesResponse>({
    queryKey: ["machines", "org", params],
    queryFn: () => api.get<ListOrgMachinesResponse>(`/api/v1/machines?${qs.toString()}`),
    enabled,
  });
}
